import { ref, Ref } from 'vue';
import { ICurrentUserStore } from './current-user-store';
import { AuthSession, IUserMetadataService, UserProfile } from '@lingvo-news/contracts';
import { retrieveRawInitData } from '@telegram-apps/sdk'
import { addApiAuthHeaderGetter, getFromApi, postToApi, onUnauthorizedResponse, resetUnauthorizedHandler } from '../requests/request-helpers';
import { isTelegramMiniApp } from './isTelegramMiniApp';
import router from '../../router/index';
import { clearStoredInvitedByUserId, readStoredInvitedByUserId } from '../../referrals/invite-links';
import { serviceContainer } from '../../store/service-container';

interface ClaimReferralResponse {
	success: boolean;
	claimed: boolean;
	reason: string;
}

export class CurrentUserStore implements ICurrentUserStore {
	private _currentUser: Ref<UserProfile | null>;
	private _initPromise: Promise<void> | null = null;

	constructor() {
		this._currentUser = ref<UserProfile | null>(null);
	}

	async initialize(): Promise<void> {
		if (!this._initPromise) {
			this._initPromise = this.initializePrivate();
		}
		await this._initPromise;
	}

	async refresh(): Promise<void> {
		this._initPromise = null;
		resetUnauthorizedHandler();
		await this.initialize();
	}

	getCurrentUserId(): string | null {
		return this._currentUser.value?.userId || null;
	}

	getCurrentUser(): UserProfile | null {
		return this._currentUser.value;
	}

	async getAuthSession(): Promise<AuthSession> {
		try {
			return await getFromApi<AuthSession>(`/api/auth/user`);
		} catch (error) {
			console.error('Failed to get auth session:', error);
			return {
				isAuthenticated: false,
				user: null,
			};
		}
	}

	async logout(): Promise<void> {
		// Optimistically clear local state so UI updates immediately.
		this._currentUser.value = null;
		this._initPromise = null;
		try {
			await postToApi(`/api/auth/logout`, {});
		} catch (error: unknown) {
			// Keep the UI logged out even if the network call fails; router guards will re-check session on next navigation.
			console.error('Failed to logout:', error);
		}
	}

	isAuthenticated(): boolean {
		return this._currentUser.value!==null;
	}

	private async initializePrivate(): Promise<void> {
		let latestData: string | undefined = undefined;
		let latestDataRetrievedAt: number = 0;

		let getInitDataCached = () => {
			let isExpired = (Date.now() - latestDataRetrievedAt) > 60000;
			if (isExpired || latestData===null) {
				latestData = retrieveRawInitData();
				latestDataRetrievedAt = Date.now();
			}
			return latestData;
		}
		if (isTelegramMiniApp()) {
			await addApiAuthHeaderGetter(() => 'tma ' + getInitDataCached());
		}
		const session = await this.getAuthSession();
		this._currentUser.value = (session.isAuthenticated && session.user) ? session.user:null;
		await this.tryClaimStoredReferral();
		this.trySendTimezone();

		onUnauthorizedResponse(() => {
			this._currentUser.value = null;
			this._initPromise = null;
			if (router.currentRoute.value.meta.requiresAuth !== false) {
				router.replace({ name: 'login' });
			}
		});
	}

	private trySendTimezone(): void {
		const userId = this._currentUser.value?.userId;
		if (!userId) return;
		try {
			const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			if (!timezone) return;
			const metadataService = serviceContainer.get(IUserMetadataService);
			metadataService.setTimezone(userId, timezone).catch(() => {});
		} catch {
			// best-effort, never block the user
		}
	}

	private async tryClaimStoredReferral(): Promise<void> {
		const currentUserId = this._currentUser.value?.userId;
		const invitedByUserId = readStoredInvitedByUserId();
		if (!currentUserId || !invitedByUserId) {
			return;
		}
		if (currentUserId===invitedByUserId) {
			clearStoredInvitedByUserId();
			return;
		}

		try {
			const response = await postToApi<ClaimReferralResponse>('/api/auth/referral/claim', {
				invitedByUserId
			});
			if (response.success) {
				clearStoredInvitedByUserId();
			}
		} catch (error) {
			console.error('Failed to claim stored referral:', error);
		}
	}
}

